import { ApplicationRef, DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  NoNewVersionDetectedEvent,
  SwUpdate,
  VersionDetectedEvent,
  VersionInstallationFailedEvent,
  VersionReadyEvent,
} from '@angular/service-worker';
import { AlertController } from '@ionic/angular/standalone';
import { concat, interval } from 'rxjs';
import { filter, first } from 'rxjs/operators';

import { VERSION } from '../version';

@Injectable({ providedIn: 'root' })
export class PwaService {
  private readonly swUpdates = inject(SwUpdate);
  private readonly applicationRef = inject(ApplicationRef);
  private readonly alertController = inject(AlertController);
  // TODO: Check si cette mécanique fonctionne bien sur Safari et firefox
  private readonly destroyRef = inject(DestroyRef);

  public init(): void {
    if (this.swUpdates.isEnabled) {
      this.swUpdates.versionUpdates
        .pipe(
          filter(
            (
              event:
                | VersionDetectedEvent
                | VersionInstallationFailedEvent
                | VersionReadyEvent
                | NoNewVersionDetectedEvent,
            ): event is VersionReadyEvent => event.type === 'VERSION_READY',
          ),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe((event: VersionReadyEvent) => {
          console.log(event);
          void this.promptUser();
        });

      const appIsStable$ = this.applicationRef.isStable.pipe(
        first((isStable: boolean) => isStable),
      );
      const everyTenMinutes$ = interval(600000);
      const everyTenMinutesOnceAppIsStable$ = concat(
        appIsStable$,
        everyTenMinutes$,
      );
      everyTenMinutesOnceAppIsStable$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          void this.swUpdates.checkForUpdate();
        });
    }
  }

  public async promptUser(): Promise<void> {
    const toast = await this.alertController.create({
      header: '✨Nouvelle version disponible',
      subHeader: VERSION.version,
      message: `Mise à jour de l'app. La page va se recharger...
      N'hésite pas à réinstaller l'app sur ton écran d'accueil ou à nous contacter si tu constates le moindre bug.`,
      backdropDismiss: false,
      buttons: ['OK'],
    });
    void toast.onDidDismiss().then(async () =>
      this.swUpdates.activateUpdate().then(() => {
        document.location.reload();
      }),
    );
    await toast.present();
  }
}
